import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { debugLogger } from '@/utils/debugLogger';

const ReactivateAccount = () => {
  const { user, refreshProfileData } = useAuth();
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);

  useEffect(() => {
    const checkDeactivationStatus = async () => {
      if (!user?.id) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('deactivated_at')
        .eq('id', user.id)
        .single();

      if (profile?.deactivated_at) {
        const deactivatedDate = new Date(profile.deactivated_at);
        const now = new Date();
        const diffTime = now.getTime() - deactivatedDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const remaining = 30 - diffDays;
        
        setDaysLeft(remaining > 0 ? remaining : 0);
      } else {
        // Se não está desativada, redirecionar para feed
        navigate('/main/feed');
      }
    };

    checkDeactivationStatus();
  }, [user, navigate]);

  const handleReactivate = async () => {
    if (!user?.id) return;

    setIsReactivating(true);
    try {
      debugLogger.debug('account', 'Iniciando reativação para user:', user.id);
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          hidden: false,
          deactivated_at: null
        })
        .eq('id', user.id);

      if (error) {
        debugLogger.error('reactivate-account', 'Erro ao atualizar banco:', error);
        throw error;
      }

      // Atualizar o estado do contexto
      await refreshProfileData();

      debugLogger.debug('account', 'Conta reativada com sucesso');
      toast.success('Conta reativada com sucesso! Bem-vindo de volta 🎉');
      navigate('/main/feed');
    } catch (error) {
      debugLogger.error('reactivate-account', 'Erro ao reativar conta:', error);
      toast.error('Erro ao reativar conta. Tente novamente.');
    } finally {
      setIsReactivating(false);
    }
  };

  if (daysLeft === null) {
    return <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-luxury-gold border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  if (daysLeft === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
        <Card className="bg-black/40 border-red-500/30 max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-400 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" />
              Período de Recuperação Expirado
            </CardTitle>
          </CardHeader>
          <CardContent className="text-white/70 space-y-4">
            <p>
              Infelizmente, o período de 30 dias para reativar sua conta expirou.
              Sua conta foi permanentemente deletada e não pode ser recuperada.
            </p>
            <Button 
              onClick={() => navigate('/auth')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Criar Nova Conta
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
      <Card className="bg-black/40 border-luxury-gold/30 max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-luxury-gold flex items-center gap-2">
            <RefreshCw className="w-6 h-6" />
            Reativar Conta
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-white/70 space-y-2">
            <p>Sua conta está desativada.</p>
            <p className="text-orange-400 font-medium">
              Você tem <strong>{daysLeft} dias</strong> restantes para reativar sua conta.
            </p>
            <p className="text-sm text-white/50">
              Após esse período, todos os seus dados serão permanentemente deletados.
            </p>
          </div>

          <div className="space-y-2">
            <Button 
              onClick={handleReactivate}
              disabled={isReactivating}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isReactivating ? 'Reativando...' : 'Reativar Minha Conta'}
            </Button>
            
            <Button 
              onClick={async () => {
                await supabase.auth.signOut();
                navigate('/auth');
              }}
              variant="outline"
              className="w-full border-border bg-background text-black hover:bg-accent hover:text-black"
            >
              Voltar para Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReactivateAccount;
